import React, { useState } from "react";
import axios from "axios";
import moment from "moment-timezone";
import { Link } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  TableRow,
  TableCell,
  Collapse,
  IconButton,
  Tooltip,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import API_BASE_URL from "../../config/api";
import { calculateDifferenceString } from "./filterUtils";
import {
  COLORS,
  MARKERS,
  getMarkerColor,
  getStatusTone,
  getTransportLed,
  isMarkerActive,
} from "./dashboardConfig";

const useStyles = makeStyles((theme) => ({
  row: {
    "&:hover > td": { backgroundColor: "#f5f9ff" },
    // Komorki z checkboxem / przyciskiem maja zerowy padding - inaczej reguła "& > td" je
    // poszerza i przypiete kolumny (left: 0 / 44 / 84) przestaja do siebie przylegac.
    "& > td.MuiTableCell-paddingCheckbox": { padding: "0 2px 0 12px" },
    "& > td.MuiTableCell-paddingNone": { padding: 0 },

    "& > td": {
      borderBottom: `1px solid ${COLORS.borderSoft}`,
      padding: "6px 12px",
      fontSize: 14,
      color: "#212121",
      backgroundColor: "#fff",
      verticalAlign: "middle",
    },
  },
  rowRevision: {
    "& > td": { backgroundColor: "#fffde7" },
    "& > td:first-child": { boxShadow: `inset 4px 0 0 ${COLORS.warning}` },
  },
  rowSelected: {
    "& > td": { backgroundColor: "#e8f1fb" },
  },
  stickyCell: {
    position: "sticky",
    zIndex: 2,
  },
  stickyEdge: {
    boxShadow: "inset -1px 0 0 #e0e0e0",
  },
  nrRef: {
    fontWeight: 700,
    color: COLORS.primary,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline", color: COLORS.primaryDark },
  },
  muted: { color: "#bdbdbd" },
  ellipsis: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pill: {
    display: "inline-block",
    padding: "3px 10px",
    borderRadius: 12,
    fontSize: 12.5,
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  transport: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#424242",
    whiteSpace: "nowrap",
  },
  markers: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 4,
    "& svg": { fontSize: 18, display: "block" },
  },
  duration: {
    fontWeight: 600,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  actions: { display: "flex", justifyContent: "flex-end", gap: 2 },
  detailCell: {
    padding: "0 !important",
    backgroundColor: "#fafcff !important",
    borderBottom: "none !important",
  },
  detailCellOpen: {
    borderBottom: `1px solid ${COLORS.border} !important`,
  },
  detailWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 3),
  },
  detailCard: {
    flex: "1 1 420px",
    minWidth: 300,
    background: "#fff",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 8,
    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  },
  detailTitle: {
    padding: theme.spacing(1, 2),
    fontSize: 15,
    fontWeight: 800,
    color: COLORS.primary,
    borderBottom: `2px solid ${COLORS.primary}`,
  },
  detailList: { maxHeight: 260, overflowY: "auto", padding: theme.spacing(0.5, 2) },
  detailItem: {
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${COLORS.borderSoft}`,
    fontSize: 13.5,
    "&:last-child": { borderBottom: "none" },
  },
  detailMeta: { color: "#777", fontSize: 12.5 },
  detailChange: { marginTop: 2 },
  detailEmpty: { padding: theme.spacing(2), color: "#9e9e9e", fontSize: 14 },
}));

const formatDateTime = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() && m.year() > 1 ? m.format("DD.MM.YYYY HH:mm") : null;
};

const formatDay = (value) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() && m.year() > 1 ? m.format("DD.MM.YYYY") : null;
};

// Jak długo odprawa jest w systemie -> kolor ostrzegawczy (tylko dla niezakończonych).
const durationColor = (row) => {
  const finished = ["Zrealizowane", "Unieważnione przez OC", "Anulowane"].includes(
    row.statusRealizacji,
  );
  if (finished || row.rodzajTransportu === "Zakończono" || !row.utworzono) {
    return "#9e9e9e";
  }
  const hours = moment().diff(moment(row.utworzono), "hours");
  if (hours >= 72) return COLORS.danger;
  if (hours >= 24) return COLORS.warning;
  return "#424242";
};

export const TransportLed = ({ transport, size = 12 }) => {
  const led = getTransportLed(transport);
  const style = {
    width: size,
    height: size,
    borderRadius: "50%",
    flexShrink: 0,
    boxSizing: "border-box",
    border: led.outlined ? "2px solid #bdbdbd" : "1px solid rgba(0,0,0,0.12)",
  };
  if (Array.isArray(led.color)) {
    style.background = `linear-gradient(90deg, ${led.color[0]} 50%, ${led.color[1]} 50%)`;
  } else {
    style.background = led.color;
  }
  return <span style={style} />;
};

const Empty = ({ classes }) => <span className={classes.muted}>—</span>;

const Row = React.memo(
  ({ row, columns, selected, onSelect, isAdmin, onDelete, tick, from = "dashboard" }) => {
    const classes = useStyles();
    const [open, setOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [editHistory, setEditHistory] = useState([]);
    const [comments, setComments] = useState([]);

    const toggleOpen = async () => {
      if (!open) {
        setDetailLoading(true);
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        };
        const [history, commentList] = await Promise.all([
          axios
            .get(`${API_BASE_URL}/history/${row.id}`, { headers })
            .then((r) => r.data)
            .catch((e) => {
              console.error("Error fetching edit history:", e);
              return [];
            }),
          axios
            .get(`${API_BASE_URL}/comments/${row.id}`, { headers })
            .then((r) => r.data)
            .catch((e) => {
              console.error("Error fetching comments:", e);
              return [];
            }),
        ]);
        setEditHistory(history);
        setComments(commentList);
        setDetailLoading(false);
      }
      setOpen((v) => !v);
    };

    const detailUrl = `/detail/${encodeURIComponent(row.nrRef)}/${encodeURIComponent(row.id)}`;
    const activeMarkers = MARKERS.filter((m) => isMarkerActive(m, row));
    const isRevision = row.rewizja === "TAK" || row.rewizja === "Tak";

    const renderCell = (id) => {
      switch (id) {
        case "transport": {
          const led = getTransportLed(row.rodzajTransportu);
          return (
            <span className={classes.transport}>
              <TransportLed transport={row.rodzajTransportu} />
              {led.label}
            </span>
          );
        }
        case "znaczniki":
          return activeMarkers.length === 0 ? (
            <Empty classes={classes} />
          ) : (
            <div className={classes.markers}>
              {activeMarkers.map(({ field, label, Icon, tone }) => (
                <Tooltip key={field} title={label} arrow>
                  <span style={{ color: getMarkerColor(tone), display: "inline-flex" }}>
                    <Icon />
                  </span>
                </Tooltip>
              ))}
            </div>
          );
        case "statusRealizacji": {
          if (!row.statusRealizacji) return <Empty classes={classes} />;
          const tone = getStatusTone(row.statusRealizacji);
          return (
            <span className={classes.pill} style={{ background: tone.bg, color: tone.fg }}>
              {row.statusRealizacji}
            </span>
          );
        }
        case "dataT1":
          return formatDay(row.data) || <Empty classes={classes} />;
        case "utworzono":
          return formatDateTime(row.utworzono) || <Empty classes={classes} />;
        case "endDate":
          return formatDateTime(row.endDate) || <Empty classes={classes} />;
        case "ileWSystemie":
          return (
            <span className={classes.duration} style={{ color: durationColor(row) }}>
              {calculateDifferenceString(row.utworzono, row.statusRealizacji)}
            </span>
          );
        case "nrAuta":
          return row.nrRejestracyjnyPojazdyKurier || <Empty classes={classes} />;
        default: {
          const value = row[id];
          if (value === null || value === undefined || value === "") {
            return <Empty classes={classes} />;
          }
          return (
            <Tooltip title={String(value)} enterDelay={600}>
              <span className={classes.ellipsis}>{value}</span>
            </Tooltip>
          );
        }
      }
    };

    return (
      <>
        <TableRow
          hover={false}
          data-tick={tick}
          className={`${classes.row} ${isRevision ? classes.rowRevision : ""} ${selected ? classes.rowSelected : ""}`}
        >
          <TableCell
            padding="checkbox"
            className={classes.stickyCell}
            style={{ left: 0, width: 52, minWidth: 52 }}
          >
            <Checkbox
              size="small"
              color="primary"
              checked={selected}
              onChange={(event) => onSelect(row, event.target.checked)}
              inputProps={{ "aria-label": `Wybierz wiersz ${row.nrRef}` }}
            />
          </TableCell>
          <TableCell
            padding="none"
            className={classes.stickyCell}
            style={{ left: 52, width: 40, minWidth: 40 }}
          >
            <Tooltip title="Historia zmian i komentarze">
              <IconButton size="small" onClick={toggleOpen} aria-label="Pokaż historię">
                {detailLoading ? (
                  <CircularProgress size={18} />
                ) : open ? (
                  <KeyboardArrowUpIcon fontSize="small" />
                ) : (
                  <KeyboardArrowDownIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>
          <TableCell
            className={`${classes.stickyCell} ${classes.stickyEdge}`}
            style={{ left: 92, width: "1%", whiteSpace: "nowrap" }}
          >
            <Link to={detailUrl} state={{ from }} className={classes.nrRef}>
              {row.nrRef}
            </Link>
          </TableCell>
          {columns.map((column) => (
            <TableCell
              key={column.id}
              style={{ minWidth: column.width, maxWidth: column.width }}
            >
              {renderCell(column.id)}
            </TableCell>
          ))}
          <TableCell style={{ width: 96, minWidth: 96 }}>
            <div className={classes.actions}>
              <Tooltip title="Otwórz szczegóły">
                <IconButton
                  size="small"
                  component={Link}
                  to={detailUrl}
                  state={{ from }}
                  aria-label="Otwórz szczegóły"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isAdmin && (
                <Tooltip title="Usuń zgłoszenie">
                  <IconButton
                    size="small"
                    onClick={() => onDelete(row)}
                    aria-label="Usuń zgłoszenie"
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell
            colSpan={columns.length + 4}
            className={`${classes.detailCell} ${open ? classes.detailCellOpen : ""}`}
          >
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div className={classes.detailWrap}>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Historia zmian</div>
                  <div className={classes.detailList}>
                    {editHistory.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak zmian.</div>
                    ) : (
                      editHistory.map((change) => (
                        <div key={change.id} className={classes.detailItem}>
                          <div>
                            <strong>{change.fieldName}</strong>
                          </div>
                          <div className={classes.detailChange}>
                            <span style={{ color: COLORS.danger }}>
                              {String(change.oldValue ?? "—")}
                            </span>
                            {" → "}
                            <span style={{ color: COLORS.success, fontWeight: 600 }}>
                              {String(change.newValue ?? "—")}
                            </span>
                          </div>
                          <div className={classes.detailMeta}>
                            {change.editedBy} · {formatDateTime(change.editedAt)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div className={classes.detailCard}>
                  <div className={classes.detailTitle}>Komentarze</div>
                  <div className={classes.detailList}>
                    {comments.length === 0 ? (
                      <div className={classes.detailEmpty}>Brak komentarzy.</div>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className={classes.detailItem}>
                          <div>{comment.comment}</div>
                          <div className={classes.detailMeta}>
                            {comment.username} · {formatDateTime(comment.created_at)}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      </>
    );
  },
);

export default Row;
